<script setup>
import { useLanguageStore } from "@/stores/languageStore.js";
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useCustomStarMapStore } from "@/stores/customStarMapStore.js";
import OriginalImageViewer from "@/components/OriginalImageViewer.vue";
import StarMapWorkshopNav from "@/components/StarMapWorkshopNav.vue";
import { generateBrickMapFromImageFile } from "@/utils/imageToBrickMap.js";
import { translateError } from "@/i18n/messages.js";

const route = useRoute();
const router = useRouter();
const languageStore = useLanguageStore();
const starMapStore = useCustomStarMapStore();

const MAP_WIDTH = 51;
const MAP_HEIGHT = 31;
const CELL = 10;
const BASE_PALETTE = [
  { symbol: "C", label: "#27FDFB", color: "#27fdfb" },
  { symbol: "B", label: "#4470CA", color: "#4470ca" },
  { symbol: "G", label: "#0FD80F", color: "#0fd80f" },
  { symbol: "Y", label: "#E4AE1F", color: "#e4ae1f" },
  { symbol: "R", label: "#D63F25", color: "#d63f25" },
  { symbol: "W", label: "#EAEAEA", color: "#eaeaea" },
  { symbol: "#", label: "BORDER", color: "#a3a3a2" },
  { symbol: " ", label: "ERASER", color: "transparent" }
];
const CUSTOM_COLOR_SYMBOLS = ["1", "2", "3", "4", "5", "6", "7", "8"];
const BASE_COLOR_SYMBOLS = ["C", "B", "G", "Y", "R", "W"];
const DEFAULT_PALETTE_SLOTS = ["C", "B", "G", "Y", "R", "W", "#", " "];
const PALETTE_VISIBLE_LIMIT = 8;

const canvas = ref(null);
const canvasScroll = ref(null);
const imageFileInput = ref(null);
const canvasZoom = ref(1);
// Mobile portrait screens have spare vertical room. Keep the full 51×31 map visible at 1×,
// but make each row only slightly taller so the editing area feels less compressed.
const CANVAS_VERTICAL_SCALE = 1.16;
const canvasBaseSize = ref({
  width: MAP_WIDTH * CELL,
  height: MAP_HEIGHT * CELL * CANVAS_VERTICAL_SCALE
});
const activeTouchPointers = new Map();
let pinchState = null;
let pinchFrame = 0;
let suppressSingleTouchUntilClear = false;
let singleTouchDrawState = null;
let canvasResizeObserver = null;
const mapId = computed(() => String(route.params.mapId || ""));
const levelNo = computed(() => Math.max(1, Number(route.params.level) || 1));
const map = computed(() => starMapStore.getMap(mapId.value));
const node = computed(() => starMapStore.getNode(mapId.value, levelNo.value));
const selectedSymbol = ref("C");
const selectedPaletteSlot = ref(0);
const drawing = ref(false);
const rows = ref([]);
const paletteSearch = ref("");
const customColorValue = ref("#27fdfb");
const customColors = ref({});
const paletteSlots = ref([...DEFAULT_PALETTE_SLOTS]);
const statusText = ref("");
const errorText = ref("");
const sourceImageDataUrl = ref("");
const sourceImageStorageKey = ref("");
const pendingSourceImageBlob = ref(null);
const loadingLevel = ref(false);
const imageGenerating = ref(false);
const originalOpen = ref(false);

const isExisting = computed(() => Boolean(node.value?.levelData?.rows));
const breakableCount = computed(() => rows.value.reduce((count, row) => count + [...row].filter(symbol => ![" ", ".", "#"].includes(symbol)).length, 0));
const canSave = computed(() => Boolean(map.value && node.value && breakableCount.value > 0));
const levelSourceImageUrl = computed(() => sourceImageDataUrl.value);
const canvasViewportStyle = computed(() => ({
  height: `${Math.max(1, canvasBaseSize.value.height)}px`
}));
const zoomStageStyle = computed(() => ({
  width: `${Math.max(1, canvasBaseSize.value.width * canvasZoom.value)}px`,
  height: `${Math.max(1, canvasBaseSize.value.height * canvasZoom.value)}px`
}));
const canvasTransformStyle = computed(() => ({
  width: `${Math.max(1, canvasBaseSize.value.width)}px`,
  height: `${Math.max(1, canvasBaseSize.value.height)}px`,
  transform: `scale(${canvasZoom.value})`
}));
function paletteItemForSymbol(symbol, slotIndex) {
  const base = BASE_PALETTE.find(item => item.symbol === symbol);
  if (base) return { ...base, slotIndex };
  const customIndex = CUSTOM_COLOR_SYMBOLS.indexOf(symbol);
  const color = customColors.value[symbol];
  if (customIndex >= 0 && color) {
    return { symbol, label: `C${customIndex + 1}`, color, slotIndex };
  }
  const fallback = BASE_PALETTE[slotIndex] || BASE_PALETTE[0];
  return { ...fallback, slotIndex };
}

const paletteItems = computed(() =>
  paletteSlots.value.slice(0, PALETTE_VISIBLE_LIMIT).map((symbol, slotIndex) =>
    paletteItemForSymbol(symbol, slotIndex)
  )
);

const visiblePalette = computed(() => {
  const query = paletteSearch.value.trim().toLowerCase();
  if (!query) return paletteItems.value;
  return paletteItems.value.filter(item =>
    item.label.toLowerCase().includes(query)
    || String(item.color || "").toLowerCase().includes(query)
  );
});

function createBlankRows() {
  const grid = Array.from({ length: MAP_HEIGHT }, () => Array.from({ length: MAP_WIDTH }, () => " "));
  for (let x = 0; x < MAP_WIDTH; x += 1) grid[0][x] = "#";
  return grid;
}

function loadRows() {
  const source = node.value?.levelData?.rows;
  if (Array.isArray(source) && source.length === MAP_HEIGHT) {
    rows.value = source.map(row => [...String(row).padEnd(MAP_WIDTH, " ").slice(0, MAP_WIDTH)]);
  } else {
    rows.value = createBlankRows();
  }
  customColors.value = { ...(node.value?.levelData?.customColors || {}) };
  sourceImageDataUrl.value = String(node.value?.levelData?.sourceImageDataUrl || "");
  sourceImageStorageKey.value = String(node.value?.levelData?.sourceImageStorageKey || "");
  pendingSourceImageBlob.value = null;
  const savedSlots = node.value?.levelData?.paletteSlots;
  paletteSlots.value = Array.isArray(savedSlots) && savedSlots.length === PALETTE_VISIBLE_LIMIT
    ? [...savedSlots]
    : [...DEFAULT_PALETTE_SLOTS];
  selectedPaletteSlot.value = 0;
  selectedSymbol.value = paletteSlots.value[0] || "C";
  customColorValue.value = colorFor(selectedSymbol.value) === "transparent"
    ? "#27fdfb"
    : colorFor(selectedSymbol.value);
  canvasZoom.value = 1;
  activeTouchPointers.clear();
  pinchState = null;
  suppressSingleTouchUntilClear = false;
  singleTouchDrawState = null;
  nextTick(() => {
    drawEditor();
    updateCanvasBaseSize();
    if (canvasScroll.value) {
      canvasScroll.value.scrollLeft = 0;
      canvasScroll.value.scrollTop = 0;
    }
  });
}

function colorFor(symbol) {
  return customColors.value[symbol] || BASE_PALETTE.find(item => item.symbol === symbol)?.color || "transparent";
}

function normalizeHexColor(value) {
  const color = String(value || "").trim();
  return /^#[0-9a-f]{6}$/i.test(color) ? color.toLowerCase() : null;
}

function symbolIsUsedInRows(symbol) {
  return rows.value.some(row => row.includes(symbol));
}

function releaseUnusedCustomColor(symbol) {
  if (!CUSTOM_COLOR_SYMBOLS.includes(symbol)) return;
  if (paletteSlots.value.includes(symbol) || symbolIsUsedInRows(symbol)) return;
  const next = { ...customColors.value };
  delete next[symbol];
  customColors.value = next;
}

function findReusableCustomSymbol() {
  const free = CUSTOM_COLOR_SYMBOLS.find(symbol => !customColors.value[symbol]);
  if (free) return free;
  return CUSTOM_COLOR_SYMBOLS.find(symbol =>
    !paletteSlots.value.includes(symbol) && !symbolIsUsedInRows(symbol)
  ) || null;
}

function selectPaletteItem(item) {
  selectedPaletteSlot.value = item.slotIndex;
  selectedSymbol.value = item.symbol;
  const color = normalizeHexColor(item.color);
  if (color) customColorValue.value = color;
}

function replaceSelectedColor() {
  const color = normalizeHexColor(customColorValue.value);
  if (!color) return;

  const slotIndex = selectedPaletteSlot.value;
  const currentSymbol = paletteSlots.value[slotIndex];
  if (currentSymbol === "#" || currentSymbol === " ") {
    statusText.value = languageStore.t("unavailable");
    return;
  }

  const builtin = BASE_PALETTE.find(item =>
    BASE_COLOR_SYMBOLS.includes(item.symbol) && normalizeHexColor(item.color) === color
  );

  const previousSymbol = currentSymbol;
  let nextSymbol = builtin?.symbol || null;

  if (!nextSymbol) {
    // 如果当前格本身就是自定义颜色，继续复用这个 symbol，已绘制的同色砖也会同步换色。
    nextSymbol = CUSTOM_COLOR_SYMBOLS.includes(currentSymbol)
      ? currentSymbol
      : findReusableCustomSymbol();
    if (!nextSymbol) {
      statusText.value = languageStore.t("unavailable");
      return;
    }
    customColors.value = { ...customColors.value, [nextSymbol]: color };
  }

  const nextSlots = [...paletteSlots.value];
  nextSlots[slotIndex] = nextSymbol;
  paletteSlots.value = nextSlots;
  selectedSymbol.value = nextSymbol;
  paletteSearch.value = "";

  if (previousSymbol !== nextSymbol) releaseUnusedCustomColor(previousSymbol);
  statusText.value = languageStore.t("selected");
  drawEditor();
}

function drawEditor() {
  const target = canvas.value;
  if (!target || rows.value.length !== MAP_HEIGHT) return;
  const ctx = target.getContext("2d");
  ctx.clearRect(0, 0, target.width, target.height);
  ctx.fillStyle = "#020913";
  ctx.fillRect(0, 0, target.width, target.height);

  for (let y = 0; y < MAP_HEIGHT; y += 1) {
    for (let x = 0; x < MAP_WIDTH; x += 1) {
      const symbol = rows.value[y][x];
      if (symbol && symbol !== " ") {
        ctx.fillStyle = colorFor(symbol);
        ctx.fillRect(x * CELL + 1, y * CELL + 1, CELL - 2, CELL - 2);
      }
      ctx.strokeStyle = "rgba(114,189,202,.10)";
      ctx.lineWidth = .5;
      ctx.strokeRect(x * CELL, y * CELL, CELL, CELL);
    }
  }
}

function updateCanvasBaseSize() {
  const target = canvas.value;
  const scroller = canvasScroll.value;
  if (!target || !scroller) return;

  // 1x means "fit the whole 51×31 map inside the viewport".
  // The canvas only becomes larger than the viewport after pinch zooming.
  const viewportWidth = scroller.clientWidth || target.offsetWidth || MAP_WIDTH * CELL;
  const width = Math.max(1, viewportWidth);
  // Keep width fitted to the phone, while giving the 31 rows ~16% more vertical room.
  // This is intentionally mild: the full map still fits at 1× and the bricks are not
  // stretched into obvious tall rectangles.
  const height = width * (MAP_HEIGHT / MAP_WIDTH) * CANVAS_VERTICAL_SCALE;
  canvasBaseSize.value = { width, height };
}

function clampZoom(value) {
  return Math.min(3.2, Math.max(1, value));
}

function getTouchPair() {
  return [...activeTouchPointers.values()].slice(0, 2);
}

function pointerDistance(a, b) {
  return Math.hypot(b.x - a.x, b.y - a.y);
}

function pointerMidpoint(a, b) {
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}

function beginPinchGesture() {
  const scroller = canvasScroll.value;
  const [a, b] = getTouchPair();
  if (!scroller || !a || !b) return;
  const rect = scroller.getBoundingClientRect();
  const midpoint = pointerMidpoint(a, b);
  const localX = midpoint.x - rect.left;
  const localY = midpoint.y - rect.top;
  pinchState = {
    startDistance: Math.max(1, pointerDistance(a, b)),
    startZoom: canvasZoom.value,
    baseFocusX: (scroller.scrollLeft + localX) / canvasZoom.value,
    baseFocusY: (scroller.scrollTop + localY) / canvasZoom.value
  };
  drawing.value = false;
  suppressSingleTouchUntilClear = true;
}

function updatePinchGesture() {
  const scroller = canvasScroll.value;
  const [a, b] = getTouchPair();
  if (!scroller || !pinchState || !a || !b) return;

  const rect = scroller.getBoundingClientRect();
  const midpoint = pointerMidpoint(a, b);
  const localX = midpoint.x - rect.left;
  const localY = midpoint.y - rect.top;
  const distance = Math.max(1, pointerDistance(a, b));
  const nextZoom = clampZoom(pinchState.startZoom * (distance / pinchState.startDistance));

  canvasZoom.value = nextZoom;
  if (pinchFrame) cancelAnimationFrame(pinchFrame);
  pinchFrame = requestAnimationFrame(() => {
    pinchFrame = 0;
    if (nextZoom <= 1.001) {
      // Back at 1x: the full map fits the viewport, so return to the complete-map view.
      scroller.scrollLeft = 0;
      scroller.scrollTop = 0;
      return;
    }
    const maxLeft = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
    const maxTop = Math.max(0, scroller.scrollHeight - scroller.clientHeight);
    scroller.scrollLeft = Math.min(maxLeft, Math.max(0, pinchState.baseFocusX * nextZoom - localX));
    scroller.scrollTop = Math.min(maxTop, Math.max(0, pinchState.baseFocusY * nextZoom - localY));
  });
}

function handleCanvasPointerDown(event) {
  event.currentTarget?.setPointerCapture?.(event.pointerId);

  if (event.pointerType === "touch") {
    activeTouchPointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

    if (activeTouchPointers.size >= 2) {
      // Two fingers always mean view navigation. Cancel any pending one-finger drawing
      // so the first finger of a pinch never leaves an accidental brick behind.
      singleTouchDrawState = null;
      beginPinchGesture();
      return;
    }

    if (suppressSingleTouchUntilClear) return;

    // Delay the first paint until we know this gesture remains one-finger.
    // A quick tap is painted on pointerup; a drag starts painting on pointermove.
    singleTouchDrawState = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      painted: false
    };
    drawing.value = false;
    return;
  }

  drawing.value = true;
  paintAtClientPoint(event.clientX, event.clientY);
}

function handleCanvasPointerMove(event) {
  if (event.pointerType === "touch" && activeTouchPointers.has(event.pointerId)) {
    activeTouchPointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

    if (activeTouchPointers.size >= 2) {
      singleTouchDrawState = null;
      if (!pinchState) beginPinchGesture();
      updatePinchGesture();
      return;
    }

    if (suppressSingleTouchUntilClear) return;

    if (singleTouchDrawState?.pointerId === event.pointerId) {
      const moved = Math.hypot(
        event.clientX - singleTouchDrawState.startX,
        event.clientY - singleTouchDrawState.startY
      );

      // A tiny threshold keeps ordinary taps stable while still allowing continuous paint.
      if (moved >= 2 || singleTouchDrawState.painted) {
        if (!singleTouchDrawState.painted) {
          paintAtClientPoint(singleTouchDrawState.startX, singleTouchDrawState.startY);
          singleTouchDrawState.painted = true;
        }
        drawing.value = true;
        paintAtClientPoint(event.clientX, event.clientY);
      }
    }
    return;
  }

  if (drawing.value) paintAtClientPoint(event.clientX, event.clientY);
}

function handleCanvasPointerEnd(event) {
  if (event.pointerType === "touch") {
    const wasSingleTap = !suppressSingleTouchUntilClear
      && singleTouchDrawState?.pointerId === event.pointerId
      && !singleTouchDrawState.painted
      && activeTouchPointers.size === 1;

    if (wasSingleTap) {
      paintAtClientPoint(event.clientX, event.clientY);
    }

    activeTouchPointers.delete(event.pointerId);
    if (singleTouchDrawState?.pointerId === event.pointerId) singleTouchDrawState = null;
    if (activeTouchPointers.size < 2) pinchState = null;
    if (activeTouchPointers.size === 0) suppressSingleTouchUntilClear = false;
  }
  drawing.value = false;
}

function pointToCell(clientX, clientY) {
  const scroller = canvasScroll.value;
  const target = canvas.value;
  if (!scroller || !target) return null;

  const viewportRect = scroller.getBoundingClientRect();
  const baseWidth = Math.max(1, canvasBaseSize.value.width || target.offsetWidth || MAP_WIDTH * CELL);
  const baseHeight = Math.max(1, canvasBaseSize.value.height || target.offsetHeight || MAP_HEIGHT * CELL);
  const zoom = Math.max(1, canvasZoom.value);

  // Convert the finger position from viewport coordinates -> zoomed scroll content ->
  // original (unzoomed) canvas coordinates. This is what lets editing remain exact
  // after pinch zooming and panning around the canvas.
  const contentX = scroller.scrollLeft + (clientX - viewportRect.left);
  const contentY = scroller.scrollTop + (clientY - viewportRect.top);
  const baseX = contentX / zoom;
  const baseY = contentY / zoom;

  if (baseX < 0 || baseY < 0 || baseX >= baseWidth || baseY >= baseHeight) return null;

  const x = Math.floor((baseX / baseWidth) * MAP_WIDTH);
  const y = Math.floor((baseY / baseHeight) * MAP_HEIGHT);
  if (x < 0 || y < 0 || x >= MAP_WIDTH || y >= MAP_HEIGHT) return null;
  return { x, y };
}

function paintAtClientPoint(clientX, clientY) {
  const cell = pointToCell(clientX, clientY);
  if (!cell) return;
  // Keep the top row as a safe roof. Users can still draw # elsewhere.
  if (cell.y === 0 && selectedSymbol.value !== "#") return;
  rows.value[cell.y][cell.x] = selectedSymbol.value;
  drawEditor();
}

function clearLevel() {
  rows.value = createBlankRows();
  statusText.value = languageStore.t("starMapCleared");
  drawEditor();
}

function uploadImageGenerate() {
  if (imageGenerating.value) return;
  imageFileInput.value?.click?.();
}


async function handleImageFile(event) {
  const input = event?.target;
  const file = input?.files?.[0];
  if (input) input.value = "";
  if (!file) return;

  if (breakableCount.value > 0) {
    const confirmed = window.confirm(languageStore.t("confirmOverwrite"));
    if (!confirmed) return;
  }

  errorText.value = "";
  statusText.value = "";
  imageGenerating.value = true;
  try {
    const result = await generateBrickMapFromImageFile(file, {
      width: MAP_WIDTH,
      height: MAP_HEIGHT,
      maxColors: 6
    });
    rows.value = result.rows.map(row => [...row]);
    customColors.value = { ...result.customColors };
    paletteSlots.value = [...result.paletteSlots];
    selectedPaletteSlot.value = 0;
    selectedSymbol.value = paletteSlots.value[0] || "C";
    customColorValue.value = colorFor(selectedSymbol.value) === "transparent"
      ? "#27fdfb"
      : colorFor(selectedSymbol.value);
    paletteSearch.value = "";
    sourceImageDataUrl.value = result.sourceImageDataUrl;
    sourceImageStorageKey.value = "";
    pendingSourceImageBlob.value = result.sourceImageBlob || null;
    canvasZoom.value = 1;
    await nextTick();
    if (canvasScroll.value) {
      canvasScroll.value.scrollLeft = 0;
      canvasScroll.value.scrollTop = 0;
    }
    drawEditor();
    statusText.value = languageStore.t("breakableCount", { count: result.breakableCount });
  } catch (error) {
    errorText.value = translateError(languageStore.language, error, "starMapImageError");
  } finally {
    imageGenerating.value = false;
  }
}


function leaveEditor() {
  router.push({ name: "workshop" });
}

async function resolveRemoteSourceImage() {
  if (!starMapStore.backendEnabled) {
    return {
      sourceImageUrl: sourceImageDataUrl.value || null,
      sourceImageStorageKey: null
    };
  }

  if (pendingSourceImageBlob.value) {
    const uploaded = await starMapStore.uploadRemoteImage(pendingSourceImageBlob.value);
    sourceImageDataUrl.value = String(uploaded?.imageUrl || "");
    sourceImageStorageKey.value = String(uploaded?.storageKey || "");
    pendingSourceImageBlob.value = null;
    if (!sourceImageDataUrl.value) {
      throw new Error(languageStore.t("starMapSaveError"));
    }
  }

  return {
    sourceImageUrl: sourceImageDataUrl.value || null,
    sourceImageStorageKey: sourceImageStorageKey.value || null
  };
}

async function saveLevel() {
  errorText.value = "";
  if (!canSave.value || loadingLevel.value || imageGenerating.value) {
    errorText.value = languageStore.t("unavailable");
    return;
  }

  imageGenerating.value = true;
  try {
    const serialized = rows.value.map(row => row.join(""));

    if (starMapStore.backendEnabled) {
      const sourceImage = await resolveRemoteSourceImage();
      const payload = {
        rows: serialized,
        customColors: customColors.value,
        paletteSlots: paletteSlots.value
      };
      if (sourceImage.sourceImageUrl) {
        payload.sourceImageUrl = sourceImage.sourceImageUrl;
        payload.sourceImageStorageKey = sourceImage.sourceImageStorageKey;
      }
      await starMapStore.saveRemoteLevel(mapId.value, levelNo.value, payload);
      loadRows();
    } else {
      starMapStore.saveLevel(mapId.value, levelNo.value, serialized, {
        customColors: customColors.value,
        paletteSlots: paletteSlots.value,
        sourceImageDataUrl: sourceImageDataUrl.value
      });
    }

    statusText.value = languageStore.t("starMapSaved");
    leaveEditor();
  } catch (error) {
    errorText.value = translateError(
      languageStore.language,
      error,
      "starMapSaveError"
    );
  } finally {
    imageGenerating.value = false;
  }
}

async function loadLevelFromAuthority() {
  loadingLevel.value = true;
  errorText.value = "";
  try {
    if (starMapStore.backendEnabled) {
      const loadedMap = await starMapStore.loadRemoteMap(mapId.value);
      if (!loadedMap || !starMapStore.getNode(mapId.value, levelNo.value)) {
        await router.replace({ name: "level-map-selector" });
        return false;
      }

      try {
        await starMapStore.loadRemoteLevel(mapId.value, levelNo.value);
      } catch (error) {
        if (Number(error?.status) === 404 || Number(error?.code) === 404005) {
          starMapStore.markRemoteLevelUnbuilt(mapId.value, levelNo.value);
        } else {
          throw error;
        }
      }
    } else {
      starMapStore.refresh();
      if (!map.value || !node.value) {
        await router.replace({ name: "level-map-selector" });
        return false;
      }
    }

    loadRows();
    return true;
  } catch (error) {
    errorText.value = translateError(
      languageStore.language,
      error,
      "starMapSaveError"
    );
    return false;
  } finally {
    loadingLevel.value = false;
  }
}

onMounted(async () => {
  const ready = await loadLevelFromAuthority();
  if (!ready) return;

  await nextTick();
  updateCanvasBaseSize();
  if (typeof ResizeObserver !== "undefined" && canvasScroll.value) {
    canvasResizeObserver = new ResizeObserver(updateCanvasBaseSize);
    canvasResizeObserver.observe(canvasScroll.value);
  }
});

onBeforeUnmount(() => {
  canvasResizeObserver?.disconnect?.();
  canvasResizeObserver = null;
  if (pinchFrame) cancelAnimationFrame(pinchFrame);
  pinchFrame = 0;
  activeTouchPointers.clear();
  singleTouchDrawState = null;
});

watch(
  () => [mapId.value, levelNo.value],
  async ([nextMapId, nextLevelNo], [previousMapId, previousLevelNo]) => {
    if (
      String(nextMapId) === String(previousMapId)
      && Number(nextLevelNo) === Number(previousLevelNo)
    ) {
      return;
    }
    await loadLevelFromAuthority();
  }
);
</script>

<template>
  <section class="level-editor page-card">
    <div class="page-header compact-level-header">
      <button class="back-button" type="button" @click="leaveEditor">← {{ languageStore.t("back") }}</button>
      <button
        class="image-button"
        type="button"
        :disabled="!levelSourceImageUrl"
        :title="languageStore.t('viewOriginal')"
        @click="originalOpen = true"
      >{{ languageStore.t("viewOriginal") }}</button>
    </div>

    <StarMapWorkshopNav />

    <div class="editor-info">
      <span>{{ isExisting ? languageStore.t("starMapEdit") : "" }}</span>
      <strong>{{ languageStore.t("breakableCount", { count: breakableCount }) }}</strong>
    </div>

    <div class="color-tools">
      <input
        v-model="paletteSearch"
        class="color-search"
        type="search"
        :placeholder="languageStore.t('searchColor')"
        :aria-label="languageStore.t('searchColor')"
      >
      <label class="custom-color-picker" :title="languageStore.t('custom')">
        <input
          v-model="customColorValue"
          type="color"
          :aria-label="languageStore.t('custom')"
          @change="replaceSelectedColor"
        >
        <span>{{ customColorValue.toUpperCase() }}</span>
      </label>
    </div>

    <div class="palette" :aria-label="languageStore.t('brickPalette')">
      <button
        v-for="item in visiblePalette"
        :key="`${item.slotIndex}-${item.symbol === ' ' ? 'eraser' : item.symbol}`"
        type="button"
        class="palette-item"
        :class="{ active: selectedPaletteSlot === item.slotIndex }"
        @click="selectPaletteItem(item)"
      >
        <span class="palette-swatch" :style="{ background: item.color }"></span>
        <small>{{ item.label }}</small>
      </button>
      <p v-if="visiblePalette.length === 0" class="palette-empty">{{ languageStore.t("noMatches") }}</p>
    </div>

    <div
      ref="canvasScroll"
      class="canvas-scroll"
      :class="{ zoomed: canvasZoom > 1.001 }"
      :style="canvasViewportStyle"
    >
      <div class="canvas-zoom-stage" :style="zoomStageStyle">
        <canvas
          ref="canvas"
          class="level-canvas"
          :style="canvasTransformStyle"
          :width="MAP_WIDTH * CELL"
          :height="MAP_HEIGHT * CELL"
          @pointerdown.prevent="handleCanvasPointerDown"
          @pointermove.prevent="handleCanvasPointerMove"
          @pointerup.prevent="handleCanvasPointerEnd"
          @pointercancel.prevent="handleCanvasPointerEnd"
        />
      </div>
    </div>

    <input
      ref="imageFileInput"
      class="image-file-input"
      type="file"
      accept="image/*"
      @change="handleImageFile"
    >

    <div class="level-actions">
      <button class="neon-button secondary" type="button" :disabled="imageGenerating" @click="uploadImageGenerate">{{ imageGenerating ? languageStore.t("starMapProcessing") : languageStore.t("starMapUpload") }}</button>
      <button class="neon-button danger" type="button" @click="clearLevel">{{ languageStore.t("starMapClear") }}</button>
      <button class="neon-button" type="button" :disabled="!canSave" @click="saveLevel">{{ languageStore.t("save") }}</button>
    </div>
    <p v-if="statusText" class="status-text">{{ statusText }}</p>
    <p v-if="errorText" class="error-text">{{ errorText }}</p>

    <OriginalImageViewer :open="originalOpen" :src="levelSourceImageUrl" :title="languageStore.t('originalImageTitle')" @close="originalOpen = false" />
  </section>
</template>

<style scoped>
.level-editor { padding: 22px; }
.compact-level-header { margin-bottom: 12px; }
.image-button {
  flex: 0 0 auto;
  border: 0;
  background: transparent;
  color: #91fff6;
  font-size: 12px;
  font-weight: 800;
  line-height: 1.15;
  white-space: nowrap;
}
.image-button:disabled { opacity: .42; cursor: not-allowed; }
.editor-info { display: flex; justify-content: space-between; gap: 12px; margin-bottom: 12px; color: rgba(255,255,255,.64); font-size: 12px; }
.editor-info strong { flex: 0 0 auto; color: #a6fff8; }
.color-tools {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 7px;
  margin-bottom: 8px;
}
.color-search {
  min-width: 0;
  min-height: 36px;
  padding: 7px 10px;
  border: 1px solid rgba(82,246,232,.22);
  border-radius: 10px;
  outline: none;
  background: rgba(3,19,31,.92);
  color: #fff;
  font: inherit;
  font-size: 11px;
}
.color-search::placeholder { color: rgba(255,255,255,.36); }
.color-search:focus { border-color: rgba(82,246,232,.75); }
.custom-color-picker {
  min-height: 36px;
  padding: 4px 7px;
  display: flex;
  align-items: center;
  gap: 6px;
  border: 1px solid rgba(82,246,232,.22);
  border-radius: 10px;
  background: rgba(3,19,31,.92);
  color: rgba(255,255,255,.72);
  font-size: 10px;
  white-space: nowrap;
}
.custom-color-picker input { width: 28px; height: 26px; padding: 0; border: 0; background: transparent; }
.palette {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 7px;
  padding: 2px 0 10px;
}
.palette-item { min-width: 0; padding: 7px 5px; border: 1px solid rgba(82,246,232,.18); border-radius: 11px; color: #fff; background: rgba(5,28,44,.82); display: grid; justify-items: center; gap: 4px; }
.palette-item.active { border-color: #52f6e8; background: rgba(15,70,83,.9); }
.palette-swatch { width: 26px; height: 18px; border: 1px solid rgba(255,255,255,.3); border-radius: 4px; display: grid; place-items: center; }
.palette-item small { max-width: 100%; overflow: hidden; text-overflow: ellipsis; font-size: 9px; white-space: nowrap; }
.palette-empty { grid-column: 1 / -1; margin: 4px 0; color: rgba(255,255,255,.5); text-align: center; font-size: 11px; }
.canvas-scroll { width: 100%; overflow: hidden; border: 1px solid rgba(82,246,232,.25); border-radius: 14px; background: #020913; touch-action: none; }
.canvas-scroll.zoomed { overflow: auto; }
.canvas-zoom-stage { position: relative; flex: 0 0 auto; }
.level-canvas { display: block; width: 100%; min-width: 0; height: auto; margin: 0; touch-action: none; image-rendering: pixelated; transform-origin: 0 0; }
.level-actions { display: flex; flex-wrap: wrap; justify-content: flex-end; gap: 8px; margin-top: 12px; }
.image-file-input { display: none; }
.status-text { margin: 6px 0 0; color: #99fff6; font-size: 12px; }
.error-text { margin: 6px 0 0; color: #ff9aa7; font-size: 12px; }

@media (max-width: 560px) {
  .level-editor {
    height: calc(100dvh - 28px);
    min-height: 0;
    margin-left: -8px;
    margin-right: -8px;
    padding: 10px 8px 12px;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }
  .compact-level-header {
    min-height: 30px;
    margin-bottom: 4px;
    align-items: center;
  }
  .compact-level-header .back-button {
    flex: 0 0 auto;
    padding: 3px 0;
    font-size: 16px !important;
    line-height: 1.1;
    white-space: nowrap;
  }
  .image-button {
    padding: 3px 0;
    font-size: 14px;
    line-height: 1.1;
    white-space: nowrap;
  }
  :deep(.star-workshop-nav) {
    margin-bottom: 5px;
  }
  .editor-info {
    min-width: 0;
    margin-bottom: 5px;
    align-items: center;
    gap: 8px;
    font-size: 11px;
    line-height: 1.2;
  }
  .editor-info > span {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .color-tools {
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 5px;
    margin-bottom: 5px;
  }
  .color-search {
    min-height: 40px;
    padding: 8px 10px;
    font-size: 11px;
  }
  .custom-color-picker {
    min-height: 40px;
    padding: 5px 7px;
    font-size: 10px;
  }
  .custom-color-picker input { width: 30px; height: 28px; }
  .palette {
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 5px;
    padding: 1px 0 6px;
  }
  .palette-item {
    min-width: 0;
    padding: 5px 3px;
    gap: 3px;
    border-radius: 10px;
  }
  .palette-swatch { width: 27px; height: 18px; }
  .palette-item small { font-size: 10px; }
  .canvas-scroll {
    flex: 0 0 auto;
    min-height: 0;
    overflow: hidden;
    border-radius: 12px;
    display: block;
    scrollbar-width: thin;
    -webkit-overflow-scrolling: touch;
    overscroll-behavior: contain;
    touch-action: none;
  }
  .canvas-scroll.zoomed { overflow: auto; }
  .level-canvas {
    display: block;
    /* 1× 仍完整显示整张 51×31 地图；仅轻微增加行高，双指放大后才超出观察框。 */
    width: 100%;
    min-width: 0;
    height: auto;
    margin: 0;
  }
  .level-actions {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 6px;
    margin-top: auto;
    padding-top: 14px;
  }
  .level-actions .neon-button {
    min-width: 0;
    min-height: 52px;
    padding: 9px 5px;
    border-radius: 13px;
    font-size: 14px;
    font-weight: 800;
    line-height: 1.15;
    white-space: nowrap;
  }
  .status-text, .error-text {
    margin: 4px 0 0;
    font-size: 10px;
    line-height: 1.2;
  }
}

@media (max-width: 380px) {
  .level-editor {
    height: calc(100dvh - 28px);
    margin-left: -6px;
    margin-right: -6px;
    padding: 8px 7px 10px;
  }
  .image-button { font-size: 13px; }
  .level-actions .neon-button {
    min-height: 50px;
    font-size: 14px;
  }
}
</style>
